/**
 * 매물 데이터를 필터링하는 유틸리티
 */

const stripNonDigits = (value = '') =>
  value?.toString().replace(/[^0-9.]/g, '') || '';

const normalizeText = (value = '') =>
  value?.toString().trim().toLowerCase() || '';

const includesText = (source, keyword) => {
  if (!keyword) return true;
  if (source === null || source === undefined) return false;
  return normalizeText(source).includes(normalizeText(keyword));
};

const parseNumber = (value) => {
  if (value === null || value === undefined) return null;
  if (typeof value === 'number') return Number.isNaN(value) ? null : value;
  const parsed = parseFloat(stripNonDigits(value));
  return Number.isNaN(parsed) ? null : parsed;
};

const matchNumericRange = (source, range) => {
  if (!range || (range.min === '' && range.max === '')) return true;

  const sourceNumber = parseNumber(source);
  if (sourceNumber === null) return false;

  // 빈 문자열을 null로 처리
  const minValue = range.min === '' || range.min === null || range.min === undefined ? null : range.min;
  const maxValue = range.max === '' || range.max === null || range.max === undefined ? null : range.max;
  
  const minNumber = minValue !== null ? parseNumber(minValue) : null;
  const maxNumber = maxValue !== null ? parseNumber(maxValue) : null;

  if (minNumber !== null && sourceNumber < minNumber) return false;
  if (maxNumber !== null && sourceNumber > maxNumber) return false;

  return true;
};

// 층수 문자열에서 숫자들을 추출 (예: "1,2층" -> [1, 2], "B1층" -> [-1], "-1층" -> [-1])
const extractFloorNumbers = (floorValue) => {
  if (!floorValue) return [];
  
  const str = floorValue.toString().trim();
  const numbers = [];
  
  // B1, B2 같은 지하층 처리 (B1, B2, B3 등)
  const basementMatch = str.match(/B(\d+)/i);
  if (basementMatch) {
    numbers.push(-parseInt(basementMatch[1], 10));
  }
  
  // -1, -2 같은 음수 표기 지하층 처리
  const negativeMatch = str.match(/-(\d+)/);
  if (negativeMatch) {
    const negativeNum = -parseInt(negativeMatch[1], 10);
    if (!numbers.includes(negativeNum)) {
      numbers.push(negativeNum);
    }
  }
  
  // 일반 층수 추출 (쉼표로 구분된 경우도 처리)
  // 음수 앞의 마이너스 기호는 제외하기 위해 더 정교한 정규식 사용
  const floorMatches = str.match(/\d+/g);
  if (floorMatches) {
    floorMatches.forEach(match => {
      const num = parseInt(match, 10);
      // 음수 앞의 숫자는 제외 (이미 처리됨)
      if (!isNaN(num) && num > 0 && !numbers.includes(num)) {
        // "-1"이나 "B1" 형태가 아닌 경우만 추가
        const beforeMatch = str.substring(0, str.indexOf(match));
        const isNegative = beforeMatch.endsWith('-');
        const isBasement = beforeMatch.match(/B$/i);
        if (!isNegative && !isBasement) {
          numbers.push(num);
        }
      }
    });
  }
  
  return numbers.length > 0 ? numbers : null;
};

const matchFloorRange = (floorValue, range) => {
  if (!range || (range.min === '' && range.max === '')) return true;
  
  const floorNumbers = extractFloorNumbers(floorValue);
  if (!floorNumbers || floorNumbers.length === 0) return false;

  const minNumber = parseNumber(range.min);
  const maxNumber = parseNumber(range.max);

  // 지하층(-1, -2 등)과 지상층(1, 2 등)을 구분하여 필터링
  // 최소값이 1 이상이면 지하층은 제외해야 함
  if (minNumber !== null && minNumber >= 1) {
    // 지하층만 있는 경우 제외
    if (floorNumbers.every(num => num < 0)) {
      return false;
    }
    // 지하층과 지상층이 섞여있으면 지상층만 확인
    const groundFloors = floorNumbers.filter(num => num > 0);
    if (groundFloors.length === 0) return false;
    
    const minFloor = Math.min(...groundFloors);
    const maxFloor = Math.max(...groundFloors);
    
    if (minNumber !== null && maxFloor < minNumber) return false;
    if (maxNumber !== null && minFloor > maxNumber) return false;
    
    return true;
  }

  // 최소값이 1 미만이거나 없으면 모든 층수(지하+지상) 확인
  const minFloor = Math.min(...floorNumbers);
  const maxFloor = Math.max(...floorNumbers);

  if (minNumber !== null && maxFloor < minNumber) return false;
  if (maxNumber !== null && minFloor > maxNumber) return false;

  return true;
};

const matchPremiumRange = (source, range) => {
  if (!range || (range.min === '' && range.max === '')) return true;

  const normalizedSource =
    source === null || source === undefined ? 0 : parseNumber(source);

  return matchNumericRange(normalizedSource, range);
};

const matchMultiSelect = (source, values) => {
  if (!Array.isArray(values) || values.length === 0) return true;
  if (source === null || source === undefined) return false;
  return values.some((value) => includesText(source, value));
};

const isFilterValueEmpty = (value) => {
  if (Array.isArray(value)) {
    return value.length === 0;
  }
  if (value && typeof value === 'object') {
    const { min, max } = value;
    return (min === undefined || min === null || min === '') &&
           (max === undefined || max === null || max === '');
  }
  return value === undefined || value === null || value === '' || value === '전체';
};

export function applyFilters(properties = [], filters = {}, consultations = []) {
  if (
    !filters ||
    Object.values(filters).every((value) => isFilterValueEmpty(value))
  ) {
    return properties;
  }

  // 관심 매물 필터링 (다른 필터보다 먼저 처리)
  let filteredProperties = properties;
  if (filters?.관심매물손님) {
    const customerName = filters.관심매물손님;
    const consultation = consultations.find(c => c.customerName === customerName);
    
    if (consultation && consultation.consultations) {
      // 해당 손님의 모든 관심 매물 수집
      const interestedProperties = new Set();
      consultation.consultations.forEach(consult => {
        if (consult.interestedProperties) {
          const props = Array.isArray(consult.interestedProperties)
            ? consult.interestedProperties
            : consult.interestedProperties.split(',').map(p => p.trim()).filter(Boolean);
          props.forEach(prop => interestedProperties.add(prop));
        }
      });
      
      // 관심 매물에 등록된 매물만 필터링 (대소문자, 공백 무시)
      filteredProperties = properties.filter(property => {
        const propertyName = normalizeText(property.상호 || property.name || '');
        return Array.from(interestedProperties).some(interestedName => 
          normalizeText(interestedName) === propertyName
        );
      });
    } else {
      // 손님을 찾을 수 없으면 빈 배열 반환
      filteredProperties = [];
    }
  }

  return filteredProperties.filter((property) => {
    const floorInfo = property['층수(해당/전체)'] || property['현층/총층수'] || '';
    const [currentFloorRaw = '', totalFloorRaw = ''] = floorInfo.split('/');

    return Object.entries(filters).every(([key, value]) => {
      if (isFilterValueEmpty(value)) return true;
      // 관심매물손님은 이미 처리했으므로 스킵
      if (key === '관심매물' || key === '관심매물손님') return true;

      switch (key) {
        case '상권':
          return matchMultiSelect(property.상권, value);
        case '층수':
          return matchFloorRange(currentFloorRaw, value);
        case '총층수':
          return matchFloorRange(totalFloorRaw, value);
        case '평수':
          return matchNumericRange(property.평수, value);
        case '보증금':
          return matchNumericRange(property.보증금, value);
        case '월세':
          return matchNumericRange(property.월세, value);
        case '관리비':
          return matchNumericRange(property.관리비, value);
        case '권리비':
          return matchPremiumRange(property.권리금, value);
        case '용도':
          return matchMultiSelect(property.용도, value);
        case '엘베':
          return includesText(property.엘베, value);
        case '화장실':
          return includesText(property.화장실, value);
        case '이름검색':
          // 상호 또는 name 필드에서 검색
          const propertyName = property.상호 || property.name || '';
          return includesText(propertyName, value);
        default:
          return true;
      }
    });
  });
}


